/**
 * Which envelope the waterfall fills first (v7.0.0 — "waterfall priority UI").
 *
 * Reordering, not numbers. The column underneath is an integer, but "priority 7"
 * means nothing to the person it is for, and a number box invites the question
 * "what should it be?" that a list never asks. The first row is filled first;
 * Move up and Move down are the whole interface.
 *
 * No dead controls: the first row has no "Move up" and the last has no "Move
 * down". The slot is held by a blank so the buttons do not jump between rows.
 *
 * This moves no money and stages nothing. It changes one thing: the order the
 * waterfall walks the envelopes in the NEXT time it is run.
 */

import { formatMoney } from '../lib/envelope-math';
import { moveInOrder, sameOrder } from '../lib/fill-order';
import { trapFocus } from '../lib/dialog-trap';
import type { ApiEnvelope } from '../lib/api';

export interface FillOrderSheetOptions {
  /** Orderable envelopes, first-filled first (lib/fill-order.fillOrder). */
  envelopes: readonly ApiEnvelope[];
  /** Save the new order, first-filled first. Rejecting leaves the sheet open. */
  onSave: (orderedIds: string[]) => Promise<void>;
  onDismiss: () => void;
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text != null) node.textContent = text;
  return node;
}

export function createFillOrderSheet(opts: FillOrderSheetOptions): HTMLElement {
  const byId = new Map(opts.envelopes.map((e) => [e.id, e]));
  const original = opts.envelopes.map((e) => e.id);
  let order = [...original];

  const backdrop = el('div', 'sheet-backdrop');
  const sheet = el('div', 'sheet');
  sheet.setAttribute('role', 'dialog');
  sheet.setAttribute('aria-modal', 'true');
  sheet.setAttribute('aria-labelledby', 'fill-order-title');

  const title = el('h2', 'sheet-title', 'Fill order');
  title.id = 'fill-order-title';
  const sub = el(
    'p',
    'sheet-sub',
    'Free cash fills these from the top. The first one is filled first.',
  );

  const list = el('ol', 'fill-order-list');
  const message = el('p', 'sheet-message');
  message.setAttribute('role', 'alert');

  const actions = el('div', 'sheet-actions');
  const cancel = el('button', 'btn-quiet', 'Not now');
  cancel.type = 'button';
  cancel.addEventListener('click', () => opts.onDismiss());
  const save = el('button', 'btn-primary', 'Save order');
  save.type = 'button';
  actions.append(cancel, save);

  const note = el(
    'p',
    'sheet-note soft',
    'This moves no money. It only decides which envelope the waterfall fills first next time.',
  );

  function render(focus?: { id: string; step: -1 | 1 }) {
    list.replaceChildren();
    order.forEach((id, index) => {
      const envelope = byId.get(id)!;
      const row = el('li', 'fill-order-row');
      row.dataset.envelopeId = id;

      const text = el('div', 'fill-order-text');
      text.append(
        el('span', 'fill-order-name', envelope.name),
        el(
          'span',
          'soft fill-order-target',
          envelope.targetMinor != null ? `target ${formatMoney(envelope.targetMinor)}` : '',
        ),
      );

      const moves = el('div', 'fill-order-moves');
      const button = (step: -1 | 1, label: string, glyph: string, shown: boolean) => {
        if (!shown) {
          // Holds the slot. Not a control, so it is hidden from everything.
          const gap = el('span', 'fill-order-gap');
          gap.setAttribute('aria-hidden', 'true');
          return gap;
        }
        const b = el('button', 'chip fill-order-move', glyph);
        b.type = 'button';
        b.dataset.step = String(step);
        b.setAttribute('aria-label', `${label}, ${envelope.name}, now number ${index + 1}`);
        b.addEventListener('click', () => {
          order = moveInOrder(order, id, step);
          message.textContent = '';
          render({ id, step });
        });
        return b;
      };
      moves.append(
        button(-1, 'Move earlier', '↑', index > 0),
        button(1, 'Move later', '↓', index < order.length - 1),
      );

      row.append(el('span', 'fill-order-rank num', String(index + 1)), text, moves);
      list.append(row);
    });

    save.disabled = sameOrder(order, original);

    if (focus) {
      // Keep the operator's place. If the moved row hit an end and that button
      // no longer exists, land on its remaining neighbour instead of losing focus.
      const row = list.querySelector<HTMLElement>(`[data-envelope-id="${CSS.escape(focus.id)}"]`);
      const same = row?.querySelector<HTMLElement>(`[data-step="${focus.step}"]`);
      (same ?? row?.querySelector<HTMLElement>('button'))?.focus();
    }
  }

  save.addEventListener('click', async () => {
    save.disabled = true;
    save.textContent = 'Saving…';
    try {
      await opts.onSave(order);
    } catch (err) {
      message.textContent =
        err instanceof Error && err.message
          ? err.message
          : "Couldn't save the order. Nothing changed.";
      save.textContent = 'Save order';
      save.disabled = sameOrder(order, original);
    }
  });

  render();
  sheet.append(title, sub, list, message, actions, note);
  backdrop.append(sheet);
  trapFocus({ backdrop, sheet, onDismiss: opts.onDismiss });
  return backdrop;
}
